// src/components/accesEnseignant/AccesEnseignantEmploye.jsx
// ============================================================================
// Onglet « Accès enseignant » du dossier de l'employé
// ============================================================================
//
// Ce que l'école fait pour qu'un enseignant ouvre son espace enseignant :
//
//   - remettre un code d'accès : le code est affiché UNE fois, avec sa fiche
//     PDF à imprimer. Le serveur n'en garde qu'une empreinte, il ne peut plus
//     jamais le relire ;
//   - voir où en est son espace (pas encore créé, actif, suspendu), lui
//     remettre un code « mot de passe oublié », suspendre (motif exigé) ou
//     réactiver son accès ;
//   - annuler un code qui n'a pas servi.
//
// Cet accès n'est PAS la connexion à l'administration (onglet « Accès et
// sécurité ») : deux portes, deux jetons signés avec des clés différentes, et
// un enseignant peut avoir l'une sans l'autre. Les deux se ferment pourtant
// ensemble quand l'employé est radié, transféré ou suspendu dans « Accès et
// sécurité » : le serveur relit ces états à chaque requête de l'espace. L'écran
// le dit, sinon l'école croirait l'enseignant connecté alors qu'il est refusé.
//
// L'enseignant ne se déclare jamais tout seul : le téléphone de la fiche
// employé n'est vérifié par personne, il ne prouve pas que celui qui crée
// l'espace est bien cet enseignant. Le code remis en main propre, si.
//
// ⚠️ Le code en clair ne vit QUE dans l'état de ce composant (`emis`) : ni
// localStorage, ni adresse, ni nom de fichier. Le dossier le démonte quand on
// quitte l'onglet ou qu'on le ferme, et le code disparaît avec lui — c'est ce
// que l'avertissement ambre annonce à l'écran.
// ============================================================================

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  KeyRound, Printer, Copy, Loader2, RotateCcw, Ban, CheckCircle2, XCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import ConfirmDialog from '../ConfirmDialog';
import { accesEnseignant, nomFicheAcces } from './apiAccesEnseignant';

// Badges de largeur fixe : ils se répètent d'une ligne à l'autre du tableau.
const BADGES_CODE = {
  valide: ['Valide', 'border-emerald-200 bg-emerald-50 text-emerald-700'],
  utilise: ['Utilisé', 'border-gray-200 bg-gray-100 text-gray-600'],
  expire: ['Expiré', 'border-amber-200 bg-amber-50 text-amber-800'],
  annule: ['Annulé', 'border-red-200 bg-red-50 text-red-700']
};

const ETATS_ESPACE = {
  aucun: ['Pas encore créé', 'border-gray-200 bg-gray-100 text-gray-600'],
  actif: ['Actif', 'border-emerald-200 bg-emerald-50 text-emerald-700'],
  suspendu: ['Suspendu', 'border-red-200 bg-red-50 text-red-700']
};

const NATURES_CODE = {
  activation: 'Activation',
  reinitialisation: 'Réinitialisation'
};

const MOTIF_MINIMUM = 5;

// Ce qui ferme l'espace sans que le compte lui-même soit suspendu : la fiche
// employé. Mêmes états que ceux que le serveur relit à chaque requête.
const COUPURES_PAR_STATUT = {
  radie: "l'employé est radié",
  transfere: "l'employé a été transféré vers un autre site",
  inactif: "l'employé est inactif"
};

// MySQL rend ses booléens calculés en 0/1 (`verrouille`, `titulaire`). Un
// `{compte.verrouille && …}` afficherait alors un « 0 » au milieu de l'écran,
// et une chaîne « 0 » passerait pour vraie : on les lit explicitement.
const estVrai = (valeur) => valeur === true || Number(valeur) === 1;

const raisonCoupure = (employe) => {
  if (!employe) return null;
  if (employe.statut && employe.statut !== 'actif') {
    return COUPURES_PAR_STATUT[employe.statut] || "l'employé n'est plus actif";
  }
  if (estVrai(employe.acces_suspendu)) {
    return 'son accès est suspendu dans « Accès et sécurité »';
  }
  return null;
};

// Affichage seulement : aucune décision ne se prend sur ces dates, le serveur
// calcule lui-même le statut des codes et le verrou des comptes.
const enDate = (valeur) => {
  if (!valeur) return null;
  const d = new Date(valeur);
  return Number.isNaN(d.getTime()) ? null : d;
};

const dateLongue = (valeur) => {
  const d = enDate(valeur);
  return d ? d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
};

const heure = (d) => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

const dateHeureLongue = (valeur) => {
  const d = enDate(valeur);
  return d ? `${dateLongue(valeur)} à ${heure(d)}` : '—';
};

const dateHeureCourte = (valeur) => {
  const d = enDate(valeur);
  return d ? `${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}, ${heure(d)}` : '—';
};

const detailCode = (k) => {
  if (k.statut === 'utilise') return `Utilisé le ${dateLongue(k.utilise_le)}`;
  if (k.statut === 'annule') return `Annulé le ${dateLongue(k.annule_le)}`;
  if (k.statut === 'expire') return `Expiré le ${dateLongue(k.expire_le)}`;
  return `Valable jusqu'au ${dateLongue(k.expire_le)}`;
};

/**
 * Les affectations de l'année regroupées comme l'enseignant les lira :
 * « 6ème A, 6ème B — Histoire-Géographie », « CM2 A — Titulaire ».
 * Une même classe peut revenir par deux tables (titulaire et matière) : elle
 * n'est écrite qu'une fois dans chaque groupe.
 */
const regrouperClasses = (classes = []) => {
  const groupes = new Map();
  classes.forEach((c) => {
    const libelle = estVrai(c.titulaire) ? 'Titulaire' : (c.matiere || 'Matière non précisée');
    if (!groupes.has(libelle)) groupes.set(libelle, []);
    const liste = groupes.get(libelle);
    if (c.classe && !liste.includes(c.classe)) liste.push(c.classe);
  });
  return [...groupes.entries()]
    .filter(([, liste]) => liste.length > 0)
    .map(([libelle, liste]) => ({ libelle, classes: liste }));
};

const libelleCreneaux = (nombre) => {
  if (!nombre) return "Aucun cours cette semaine dans l'emploi du temps";
  return `${nombre} cours cette semaine dans l'emploi du temps`;
};

const Badge = ({ classes, children }) => (
  <span className={`inline-flex h-6 w-[132px] items-center justify-center whitespace-nowrap rounded-full border text-xs font-medium ${classes}`}>
    {children}
  </span>
);

// Les deux actions du compte se côtoient : même taille, même largeur, pour ne
// pas suggérer que l'une compte plus que l'autre.
const BOUTON_ACTION_CLASSES = 'min-w-[190px] gap-2 font-semibold';

/** Le code tout juste émis : affiché une seule fois, avec sa fiche. */
const CodeEmis = ({ emis, nomFichier, onFermer, onToast }) => {
  const [impression, setImpression] = useState(false);
  const reinitialisation = emis.type === 'reinitialisation';

  const imprimer = async () => {
    setImpression(true);
    try {
      await accesEnseignant.fiche(emis.code, nomFichier);
    } catch (e) {
      onToast?.(e.message, 'error');
    } finally {
      setImpression(false);
    }
  };

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(emis.code);
      onToast?.('Code copié.', 'success');
    } catch {
      onToast?.('Copie impossible : notez le code à la main.', 'error');
    }
  };

  return (
    <div>
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          {reinitialisation ? 'Code de réinitialisation' : "Code d'activation"} — à remettre à l'enseignant
        </p>
        <p className="mt-1 font-mono text-[30px] font-semibold tracking-[0.18em] text-[#246990]">{emis.code}</p>
        <p className="mt-1.5 text-xs text-gray-500">Valable jusqu'au {dateLongue(emis.expire_le)}, une seule fois</p>
      </div>

      <div className="mt-3 flex gap-2">
        <Button size="default" onClick={imprimer} disabled={impression} className="flex-1 gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a]">
          {impression ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
          Fiche à imprimer
        </Button>
        <Button size="default" variant="outline" onClick={copier} className="flex-1 gap-2">
          <Copy className="h-4 w-4" />
          Copier
        </Button>
      </div>

      {reinitialisation && (
        <p className="mt-2.5 text-xs leading-relaxed text-gray-600">
          L'enseignant le saisit dans « Mot de passe oublié », sur la page de connexion de son espace, avec son
          numéro de téléphone habituel : il choisit alors un nouveau mot de passe.
        </p>
      )}

      <p className="mt-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800">
        Le code ne s'affiche qu'une fois. Une fois ce dossier fermé ou cet onglet quitté, il faudra en générer un nouveau.
      </p>

      <button type="button" onClick={onFermer} className="mt-2 text-xs font-semibold text-[#246990] hover:underline">
        C'est fait, masquer le code
      </button>
    </div>
  );
};

const AccesEnseignantEmploye = ({ employeId, onToast }) => {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(null);
  const [emis, setEmis] = useState(null);
  const [action, setAction] = useState(null); // { type: 'suspendre'|'annuler', cible }
  const [motif, setMotif] = useState('');
  const [confirmation, setConfirmation] = useState(false);
  const idMotif = useId();
  const idAideMotif = useId();

  // Chaque chargement porte un numéro : seule la réponse du DERNIER s'affiche.
  // Sans cela, la lecture lancée à l'ouverture pouvait revenir après celle qui
  // suit une action et remettre à l'écran l'état d'avant (un code « Valide »
  // qu'on vient d'annuler, ou l'employé précédent).
  const demande = useRef(0);

  const charger = useCallback(async () => {
    const numero = ++demande.current;
    setErreur(null);
    try {
      const reponse = await accesEnseignant.employe(employeId);
      if (numero === demande.current) setDonnees(reponse.data);
    } catch (e) {
      if (numero === demande.current) setErreur(e.message);
    }
  }, [employeId]);

  // Un autre employé : on repart de zéro, et surtout sans le code affiché pour
  // le précédent.
  useEffect(() => {
    setDonnees(null);
    setEmis(null);
    setAction(null);
    setMotif('');
    charger();
  }, [charger]);

  // Un refus du serveur dit souvent que l'écran est en retard sur la base : un
  // compte créé entre-temps (409 COMPTE_EXISTANT), un employé radié depuis
  // l'ouverture du dossier (403 ACCES_RETIRE), un code utilisé ou annulé
  // ailleurs. On relit, pour que l'écran cesse de proposer ce que le serveur
  // vient de refuser. Une panne réseau (pas de statut) ne change rien à l'état.
  const relireApresRefus = async (e) => {
    if (e?.statut) await charger();
  };

  const executer = async (cle, travail) => {
    setEnCours(cle);
    try {
      await travail();
    } catch (e) {
      onToast?.(e.message, 'error');
      await relireApresRefus(e);
    } finally {
      setEnCours(null);
    }
  };

  const creerCode = () => executer('code', async () => {
    const reponse = await accesEnseignant.creerCode(employeId);
    setEmis({ ...reponse.data, type: 'activation' });
    await charger();
  });

  const reinitialiser = (compte) => executer('reinit', async () => {
    const reponse = await accesEnseignant.reinitialiser(compte.id);
    setEmis({ ...reponse.data, type: 'reinitialisation' });
    await charger();
  });

  const reactiver = (compte) => executer('statut', async () => {
    const reponse = await accesEnseignant.statutCompte(compte.id, 'actif');
    onToast?.(reponse?.message || "L'accès à l'espace enseignant est rétabli.", 'success');
    await charger();
  });

  const fermerAction = () => {
    setAction(null);
    setMotif('');
  };

  const confirmerAction = async () => {
    if (!action || confirmation) return;
    const { type, cible } = action;
    setConfirmation(true);
    try {
      let reponse;
      if (type === 'suspendre') reponse = await accesEnseignant.statutCompte(cible.id, 'suspendu', motif.trim());
      if (type === 'annuler') reponse = await accesEnseignant.annulerCode(cible.id);
      onToast?.(
        reponse?.message || (type === 'suspendre' ? "L'accès à l'espace enseignant est suspendu." : 'Code annulé.'),
        'success'
      );
      // Le code encore affiché vient d'être annulé : le laisser à l'écran
      // inviterait à imprimer une fiche qui ne marche plus.
      if (type === 'annuler' && emis?.id === cible.id) setEmis(null);
      fermerAction();
      await charger();
    } catch (e) {
      onToast?.(e.message, 'error');
      // Un motif refusé (400, 422) se corrige dans la fenêtre : on la laisse
      // ouverte. Tout autre refus (code déjà utilisé ou annulé ailleurs, droit
      // retiré, compte disparu) ôte son objet à la fenêtre : on la ferme et on
      // relit.
      if (e?.statut && e.statut !== 400 && e.statut !== 422) {
        fermerAction();
        await relireApresRefus(e);
      }
    } finally {
      setConfirmation(false);
    }
  };

  if (!donnees) {
    if (erreur) {
      return (
        <div role="alert" className="m-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {erreur}
          <button type="button" onClick={charger} className="ml-3 font-semibold underline">Réessayer</button>
        </div>
      );
    }
    return (
      <div role="status" className="flex items-center justify-center gap-2 p-10 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin text-[#246990]" />
        Chargement de l'accès enseignant…
      </div>
    );
  }

  const { droits, employe, annee, compte } = donnees;
  const classes = Array.isArray(donnees.classes) ? donnees.classes : [];
  const codes = Array.isArray(donnees.codes) ? donnees.codes : [];
  const peutGerer = Boolean(droits?.gerer);
  // Une action à la fois : pendant qu'un code se génère, « Suspendre » ou
  // « Annuler ce code » partiraient sur un état que la réponse va changer.
  const occupe = Boolean(enCours) || confirmation;
  const coupure = raisonCoupure(employe);
  const etat = compte ? (compte.statut === 'suspendu' ? 'suspendu' : 'actif') : 'aucun';
  const [libelleEtat, classesEtat] = ETATS_ESPACE[etat];
  // Le code qui attend encore d'être saisi : d'activation tant que l'espace
  // n'existe pas, de réinitialisation ensuite.
  const codeEnAttente = codes.find((k) => k.statut === 'valide' && k.type === (compte ? 'reinitialisation' : 'activation'));
  const peutGenerer = !compte && !coupure;
  const groupes = regrouperClasses(classes);
  const motifValide = motif.trim().length >= MOTIF_MINIMUM;

  // Sans le droit de gérer, l'écran décrit l'état sans inviter à une action
  // que la personne ne peut pas faire.
  let texteCode;
  if (!compte && coupure) {
    texteCode = `Aucun code ne peut être remis : ${coupure}.`;
  } else if (compte && codeEnAttente) {
    texteCode = `Un code de réinitialisation émis le ${dateLongue(codeEnAttente.cree_le)} est valable jusqu'au ${dateLongue(codeEnAttente.expire_le)}.`
      + (peutGerer ? " Il ne peut plus être affiché : s'il a été perdu, « Mot de passe oublié » en génère un autre et annule celui-ci." : '');
  } else if (compte) {
    texteCode = peutGerer
      ? "Aucun code en cours. L'espace est déjà créé : en cas de mot de passe perdu, utilisez « Mot de passe oublié »."
      : "Aucun code en cours. L'espace est déjà créé.";
  } else if (codeEnAttente) {
    texteCode = `Un code émis le ${dateLongue(codeEnAttente.cree_le)} est valable jusqu'au ${dateLongue(codeEnAttente.expire_le)}.`
      + (peutGerer ? " Il ne peut plus être affiché : s'il a été perdu, générez-en un nouveau, l'ancien sera annulé." : '');
  } else {
    texteCode = peutGerer
      ? "Aucun code en cours. Générez un code et remettez-le à l'enseignant en main propre, avec sa fiche à imprimer."
      : 'Aucun code en cours.';
  }

  return (
    <div className="space-y-4 p-5 pb-10">
      <p className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] px-3.5 py-3 text-[13px] leading-relaxed text-gray-700">
        L'accès enseignant ouvre l'<strong className="font-semibold">espace enseignant</strong>, où l'enseignant retrouve
        ses classes de l'année et les informations de l'école. Il est distinct de la connexion à l'administration,
        réglée dans « Accès et sécurité ». La radiation ou le transfert de l'employé, comme la suspension de son accès
        dans « Accès et sécurité », le coupent pourtant aussitôt.
      </p>

      {!peutGerer && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          Vous pouvez consulter cet accès, mais pas le modifier : il faut, dans le module Ressources humaines, la
          permission « Espace enseignant : remettre un code d'accès à un enseignant… ».
        </p>
      )}

      {/* Un rechargement manqué après une action ne doit pas effacer l'écran :
          le code tout juste émis y est peut-être encore affiché. */}
      {erreur && (
        <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          Les informations n'ont pas pu être rafraîchies : {erreur}
          <button type="button" onClick={charger} className="ml-2 font-semibold underline">Réessayer</button>
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,6fr)]">
        <section className="rounded-lg border p-4">
          <h3 className="text-sm font-semibold text-gray-900">Espace enseignant</h3>
          <p className="mt-3">
            <span className={`inline-flex h-6 items-center whitespace-nowrap rounded-full border px-2.5 text-xs font-medium ${classesEtat}`}>
              {libelleEtat}
            </span>
          </p>

          {coupure && (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs leading-relaxed text-red-700">
              Accès coupé : {coupure}. L'espace enseignant reste fermé tant que cela dure, même avec un compte actif
              ou un code valable.
            </p>
          )}

          {compte ? (
            <>
              <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 text-[13px] sm:grid-cols-[170px_minmax(0,1fr)] sm:gap-y-2">
                <dt className="text-gray-500">Identifiant de connexion</dt>
                <dd className="mb-1 text-gray-900 sm:mb-0">{compte.telephone_masque || '—'}</dd>
                <dt className="text-gray-500">Créé le</dt>
                <dd className="mb-1 text-gray-900 sm:mb-0">{dateLongue(compte.cree_le)}</dd>
                <dt className="text-gray-500">Dernière connexion</dt>
                <dd className="mb-1 text-gray-900 sm:mb-0">
                  {compte.derniere_connexion ? dateHeureLongue(compte.derniere_connexion) : 'Jamais connecté'}
                </dd>
                {etat === 'suspendu' && (
                  <>
                    <dt className="text-gray-500">Suspendu le</dt>
                    <dd className="mb-1 text-gray-900 sm:mb-0">{dateLongue(compte.suspendu_le)}</dd>
                    <dt className="text-gray-500">Motif</dt>
                    <dd className="mb-1 break-words text-gray-900 sm:mb-0">{compte.suspendu_motif || '—'}</dd>
                  </>
                )}
              </dl>

              {estVrai(compte.verrouille) && (
                <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
                  Connexion verrouillée quelques minutes après plusieurs mots de passe erronés. Le verrou se lève seul,
                  ou avec un code « Mot de passe oublié ».
                </p>
              )}

              {peutGerer && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className={BOUTON_ACTION_CLASSES}
                    disabled={occupe}
                    onClick={() => reinitialiser(compte)}
                  >
                    {enCours === 'reinit' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                    Mot de passe oublié
                  </Button>
                  {etat === 'suspendu' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className={`${BOUTON_ACTION_CLASSES} border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800`}
                      disabled={occupe}
                      onClick={() => reactiver(compte)}
                    >
                      {enCours === 'statut' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Réactiver l'accès
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className={`${BOUTON_ACTION_CLASSES} border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800`}
                      disabled={occupe}
                      onClick={() => { setMotif(''); setAction({ type: 'suspendre', cible: compte }); }}
                    >
                      <Ban className="h-4 w-4" />
                      Suspendre l'accès
                    </Button>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="mt-3 text-[13px] text-gray-600">
              L'enseignant crée son espace avec le code d'accès remis par l'école : il choisit alors son numéro de
              téléphone et son mot de passe.
            </p>
          )}

          <div className="mt-4 border-t border-gray-100 pt-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {compte ? "Ce que l'espace lui montre" : "Ce que l'espace lui montrera"}{annee?.nom ? ` — ${annee.nom}` : ''}
            </p>
            {!annee ? (
              <p className="mt-1.5 text-[13px] text-gray-500">
                Aucune année scolaire n'est active : l'espace s'ouvrira sans classes.
              </p>
            ) : groupes.length === 0 ? (
              <p className="mt-1.5 text-[13px] text-gray-500">
                Aucune classe ne lui est affectée pour {annee.nom}. L'espace s'ouvrira quand même, sans classes,
                jusqu'à ce que ses affectations soient faites.
              </p>
            ) : (
              <ul className="mt-1.5 list-disc space-y-0.5 pl-[18px] text-[13px] leading-relaxed text-gray-700">
                {groupes.map((g) => (
                  <li key={g.libelle}>{g.classes.join(', ')} — {g.libelle}</li>
                ))}
                <li>{libelleCreneaux(Number(donnees.creneaux_semaine) || 0)}</li>
              </ul>
            )}
          </div>
        </section>

        <section className="rounded-lg border p-4">
          <h3 className="text-sm font-semibold text-gray-900">Code d'accès</h3>
          {/* Zone annoncée aux lecteurs d'écran : le code qui vient d'être
              émis s'y lit sans qu'il faille aller le chercher. */}
          <div className="mt-3" aria-live="polite">
            {emis ? (
              <CodeEmis
                emis={emis}
                nomFichier={nomFicheAcces(employe)}
                onFermer={() => setEmis(null)}
                onToast={onToast}
              />
            ) : (
              <>
                <p className="text-[13px] leading-relaxed text-gray-600">{texteCode}</p>
                {peutGerer && (
                  <Button
                    size="default"
                    onClick={creerCode}
                    disabled={!peutGenerer || occupe}
                    className="mt-3 min-w-[220px] gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a]"
                  >
                    {enCours === 'code' ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                    {codeEnAttente && !compte ? 'Générer un nouveau code' : "Générer un code d'accès"}
                  </Button>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      <section className="rounded-lg border p-4">
        <h3 className="text-sm font-semibold text-gray-900">Codes émis</h3>
        {codes.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">Aucun code émis pour cet employé.</p>
        ) : (
          <div className="mt-2.5 overflow-x-auto">
            <table className="w-full min-w-[680px] border-collapse text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th scope="col" className="border-b border-gray-200 px-2.5 py-2 font-semibold">Émis le</th>
                  <th scope="col" className="border-b border-gray-200 px-2.5 py-2 font-semibold">Nature</th>
                  <th scope="col" className="border-b border-gray-200 px-2.5 py-2 font-semibold">Par</th>
                  <th scope="col" className="border-b border-gray-200 px-2.5 py-2 font-semibold">Statut</th>
                  <th scope="col" className="w-[170px] border-b border-gray-200 px-2.5 py-2 font-semibold">
                    <span className="sr-only">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {codes.map((k) => {
                  const [libelle, classesBadge] = BADGES_CODE[k.statut] || [k.statut, 'border-gray-200 bg-gray-100 text-gray-600'];
                  return (
                    <tr key={k.id}>
                      <td className="border-b border-gray-100 p-2.5">
                        <p className="text-gray-800">{dateHeureCourte(k.cree_le)}</p>
                        <p className="text-[11px] text-gray-500">{detailCode(k)}</p>
                      </td>
                      <td className="border-b border-gray-100 p-2.5 text-gray-700">{NATURES_CODE[k.type] || k.type}</td>
                      <td className="border-b border-gray-100 p-2.5 text-gray-700">{k.cree_par_nom || '—'}</td>
                      <td className="border-b border-gray-100 p-2.5"><Badge classes={classesBadge}>{libelle}</Badge></td>
                      <td className="border-b border-gray-100 p-2.5">
                        {peutGerer && k.statut === 'valide' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-[150px] gap-2 font-semibold"
                            disabled={occupe}
                            // Le libellé visible se répète à chaque ligne : le
                            // lecteur d'écran doit savoir LEQUEL il annule.
                            aria-label={`Annuler ce code (${NATURES_CODE[k.type] || k.type}, émis le ${dateHeureCourte(k.cree_le)})`}
                            onClick={() => setAction({ type: 'annuler', cible: k })}
                          >
                            <XCircle className="h-4 w-4" />
                            Annuler ce code
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ConfirmDialog
        isOpen={Boolean(action)}
        onClose={fermerAction}
        onConfirm={confirmerAction}
        isLoading={confirmation}
        type={action?.type === 'annuler' ? 'warning' : 'error'}
        title={{
          suspendre: "Suspendre l'accès à l'espace enseignant",
          annuler: 'Annuler ce code'
        }[action?.type] || ''}
        description={{
          suspendre: "L'enseignant est déconnecté aussitôt et ne pourra plus ouvrir son espace jusqu'à la réactivation. "
            + "Sa connexion à l'administration, réglée dans « Accès et sécurité », n'est pas touchée.",
          annuler: "Le code ne pourra plus être utilisé. L'enseignant devra en recevoir un nouveau."
        }[action?.type] || ''}
        confirmText={action?.type === 'annuler' ? 'Annuler le code' : "Suspendre l'accès"}
        cancelText="Retour"
        confirmDisabled={action?.type === 'suspendre' && !motifValide}
      >
        {action?.type === 'suspendre' && (
          <div>
            <label htmlFor={idMotif} className="block text-sm text-gray-700">
              Motif (obligatoire)
            </label>
            <textarea
              id={idMotif}
              className="mt-1 w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
              rows={3}
              maxLength={255}
              required
              aria-required="true"
              aria-invalid={motif.length > 0 && !motifValide}
              aria-describedby={idAideMotif}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
            <p id={idAideMotif} className="mt-1 text-[11px] text-gray-500">
              {MOTIF_MINIMUM} caractères au moins. Le motif reste affiché dans cet onglet tant que l'accès est suspendu.
            </p>
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
};

export default AccesEnseignantEmploye;
